import { TRACKING_CONFIG } from '@intent/shared/tracking';
import type { EventQueue } from './event-queue';
import type { Tracker } from './tracker';

/**
 * Periodic liveness signal.
 *
 * This is load-bearing for two features that would otherwise be impossible.
 * A rule like "time on page > 60 seconds" can never fire from a page view
 * alone — a visitor reading the pricing page emits nothing while they read —
 * so the heartbeat supplies the clock. It doubles as the presence signal that
 * marks a visitor online in the dashboard.
 *
 * Ticks pause while the tab is hidden: a backgrounded tab is not a visitor
 * reading the page, and counting it would inflate both time-on-page and the
 * intent score.
 */
export class Heartbeat {
  private timer: number | null = null;

  constructor(
    private readonly queue: EventQueue,
    private readonly tracker: Tracker,
  ) {}

  start(): void {
    this.stop();

    this.timer = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;

      this.queue.enqueue('HEARTBEAT', { timeOnPage: this.tracker.timeOnPage });
    }, TRACKING_CONFIG.heartbeatIntervalMs);
  }

  stop(): void {
    if (this.timer !== null) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }
}
